<script setup lang="ts">
import { apiPreferences } from '@api/preferences';
import BaseButton from '@components/quiz_v2/BaseButton/BaseButton.vue';
import BaseCheckbox from '@components/quiz_v2/BaseCheckbox/BaseCheckbox.vue';
import type { Preferences } from '@models/preferences';
import { cloneDeep, isEqual } from 'lodash';
import { computed, ref } from 'vue';
import { useToast } from 'vue-toast-notification';

/** JRNYS is not doing SMS yet — hide SMS prefs in UI until infra is ready. */
const SHOW_SMS_PREFERENCES = false;

const props = defineProps<{
  preferences?: Preferences;
  token?: string;
  email?: string;
  userExist?: boolean;
  smsStop?: boolean;
}>();

const toast = useToast();
const loading = ref(false);

const getInitialPreferences = (): Preferences | null => {
  if (!props.preferences) return null;
  const prefs = cloneDeep(props.preferences);
  if (props.smsStop) {
    prefs.smsMarketing = false;
    prefs.smsTransactional = false;
  }
  return prefs;
};

const localPreferences = ref<Preferences | null>(getInitialPreferences());
const initialPreferences = ref<Preferences | null>(getInitialPreferences());

const hasChanges = computed(() => !isEqual(initialPreferences.value, localPreferences.value));
const allFalse = computed(() => {
  if (!localPreferences.value) return true;
  const emailPrefs = {
    marketingGeneric: localPreferences.value.marketingGeneric,
    marketingPersonalizedNonPHI: localPreferences.value.marketingPersonalizedNonPHI,
    marketingPersonalizedPHI: localPreferences.value.marketingPersonalizedPHI,
  };
  return Object.values(emailPrefs).every((value) => value === false);
});

const handleUnsubscribeFromAll = () => {
  if (!localPreferences.value) return;

  localPreferences.value = {
    ...localPreferences.value,
    marketingGeneric: false,
    marketingPersonalizedNonPHI: false,
    marketingPersonalizedPHI: false,
    smsMarketing: false,
    smsTransactional: false,
  };
};

const handleUpdate = async (token?: string, prefs?: Preferences) => {
  if (!token || !prefs) return;

  try {
    loading.value = true;
    const response = await apiPreferences.updatePreferences({
      token,
      preferences: prefs,
    });

    if (!response.data?.success) {
      toast.error('Failed to save preferences');
      return;
    }

    initialPreferences.value = cloneDeep(localPreferences.value) ?? null;
    toast.success('Preferences updated successfully');
  } catch {
    toast.error('Failed to save preferences');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <section class="flex size-full max-w-600 flex-col gap-24 rounded-8 bg-white p-24 shadow-md lg:p-32">
    <div class="flex flex-col gap-8">
      <h1 class="text-21 font-secondary font-medium lg:text-24">
        {{ userExist ? 'Communication preferences' : 'Email preferences' }}
      </h1>
      <span class="text-14 text-secondary-text">{{ email }}</span>
    </div>

    <template v-if="userExist">
      <p class="text-14 leading-normal text-off-black-500">
        Choose how you'd like to receive marketing emails from JRNYS.
      </p>

      <template v-if="localPreferences">
        <div class="flex flex-col gap-20">
          <h2 class="text-12 font-medium uppercase tracking-wide text-secondary-text">Email</h2>

          <BaseCheckbox v-model="localPreferences.marketingGeneric" :disabled="loading">
            <span class="text-14 leading-normal text-off-black-500">
              Receive general marketing emails
            </span>
          </BaseCheckbox>

          <BaseCheckbox
            v-model="localPreferences.marketingPersonalizedNonPHI"
            :disabled="loading"
          >
            <span class="text-14 leading-normal text-off-black-500">
              Receive personalized marketing emails based on my quiz responses
            </span>
          </BaseCheckbox>

          <BaseCheckbox v-model="localPreferences.marketingPersonalizedPHI" :disabled="loading">
            <span class="text-14 leading-normal text-off-black-500">
              Receive personalized marketing emails based on my health or treatment information
            </span>
          </BaseCheckbox>
        </div>

        <div v-if="SHOW_SMS_PREFERENCES" class="flex flex-col gap-20">
          <h2 class="text-12 font-medium uppercase tracking-wide text-secondary-text">
            SMS preferences
          </h2>

          <BaseCheckbox v-model="localPreferences.smsMarketing" :disabled="loading || smsStop">
            <span class="text-14 leading-normal text-off-black-500">
              Receive occasional promotional texts from JRNYS
            </span>
          </BaseCheckbox>

          <BaseCheckbox
            v-model="localPreferences.smsTransactional"
            :disabled="loading || smsStop"
          >
            <span class="text-14 leading-normal text-off-black-500">
              Receive transactional texts about my orders and account activity
            </span>
          </BaseCheckbox>

          <p v-if="smsStop" class="text-14 text-secondary-text">
            You opted out of SMS by replying STOP. To re-enable, reply START to any of our messages.
          </p>
        </div>

        <div class="mt-8 flex flex-col gap-16 border-t border-shell pt-24">
          <button
            type="button"
            class="text-14 text-secondary-text underline transition-colors hover:text-off-black-500 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-40"
            :disabled="allFalse || loading"
            @click="handleUnsubscribeFromAll"
          >
            Unsubscribe from all email marketing
          </button>

          <BaseButton
            class="w-full"
            color="black"
            size="sm"
            :loading="loading"
            :disabled="!hasChanges || loading"
            @click="handleUpdate(token, localPreferences)"
          >
            Save preferences
          </BaseButton>

          <p
            v-if="!hasChanges"
            class="text-12 text-center text-secondary-text"
          >
            Change a preference above to enable save.
          </p>
        </div>
      </template>
    </template>

    <template v-else>
      <p class="text-14 leading-normal text-off-black-500">
        Click below to unsubscribe from marketing emails.
      </p>

      <BaseButton
        class="w-full"
        theme="outlined"
        :loading="loading"
        @click="handleUpdate(token, { marketingGeneric: false })"
      >
        Unsubscribe
      </BaseButton>
    </template>
  </section>
</template>
